<script lang="js" setup>
import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import { hexToRgb } from '@core/utils/colorConverter'

const config = useRuntimeConfig()
const { global } = useTheme()
const { isMobile } = useDevice()

// Initialize core and config store
initCore()
initConfigStore()

const configStore = useConfigStore()

// Set vertical nav for mobile devices
if (isMobile) {
  configStore.appContentLayoutNav = 'vertical'
}

// Global UI state
const { snackbarState, hideSnackbar } = useSnackbar()
const { dialogState, closeDialog } = useDialog()

// Loading state for page transitions
const nuxtApp = useNuxtApp()
const isPageLoading = ref(false)

nuxtApp.hook('page:start', () => {
  isPageLoading.value = true
})

nuxtApp.hook('page:finish', () => {
  isPageLoading.value = false
})

// Load external CSS from API
useHead({
  link: [
    {
      id: 'abaIntake-css',
      rel: 'stylesheet',
      type: 'text/css',
      href: `${config.public.apiBaseUrl}css/abaIntake.css?v=1.0.1`,
    },
  ],
})
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <VApp
      :style="`--v-global-theme-primary: ${hexToRgb(
        global.current.value.colors.primary
      )}`"
    >
      <!-- Page Loading Indicator -->
      <VProgressLinear
        v-if="isPageLoading"
        indeterminate
        color="primary"
        height="3"
        class="app-loading-bar"
      />

      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>

      <!-- Global Snackbar -->
      <VSnackbar
        v-model="snackbarState.visible"
        :color="snackbarState.color"
        :timeout="snackbarState.timeout"
        location="top right"
        @click:close="hideSnackbar"
      >
        <div class="d-flex align-center gap-2">
          <VIcon
            v-if="snackbarState.icon"
            :icon="snackbarState.icon"
            size="20"
          />
          <span>{{ snackbarState.message }}</span>
        </div>

        <template #actions>
          <VBtn
            icon
            size="small"
            variant="text"
            @click="hideSnackbar"
          >
            <VIcon
              icon="tabler-x"
              size="18"
            />
          </VBtn>
        </template>
      </VSnackbar>

      <!--
        Global Confirm Dialog — wearing the app's OWN modal shell (Sandy
        2026-08-18): tinted `fm-header` strip with a tonal avatar and icon,
        divider, small buttons. Same as every admin modal and
        `StrongDeleteDialog`, so a confirm no longer reads as a stray browser
        box. The icon follows the confirm colour (see `iconFor` in useDialog).
      -->
      <VDialog
        v-model="dialogState.visible"
        max-width="440"
        persistent
      >
        <!--
          `fm-form` is the app's modal shell (assets/styles/styles.scss): it's
          what paints the tinted header strip every admin modal wears. Adding
          it here is what makes a confirm look like the rest of the app
          instead of a stray browser box.
        -->
        <VCard class="fm-form">
          <VCardItem
            v-if="dialogState.title"
            class="fm-header"
          >
            <template #prepend>
              <VAvatar
                :color="dialogState.confirmColor || 'primary'"
                variant="tonal"
                rounded="lg"
                size="32"
              >
                <VIcon
                  :icon="dialogState.icon || 'tabler-help-circle'"
                  size="17"
                />
              </VAvatar>
            </template>
            <VCardTitle class="text-subtitle-1 font-weight-bold">
              {{ dialogState.title }}
            </VCardTitle>
          </VCardItem>

          <VCardText class="pt-4 pb-2 text-body-2">
            {{ dialogState.message }}
          </VCardText>

          <!--
            Cancel on the LEFT edge, the action on the RIGHT (Sandy
            2026-08-18) — pushed to opposite ends, exactly like
            `StrongDeleteDialog`: the way out and the way through are never
            adjacent, so neither is hit by accident.
          -->
          <VCardActions class="pa-4 gap-2">
            <VBtn
              v-if="dialogState.showCancel"
              color="secondary"
              variant="tonal"
              density="comfortable"
              @click="dialogState.onCancel && dialogState.onCancel()"
            >
              {{ dialogState.cancelText }}
            </VBtn>

            <VSpacer />

            <VBtn
              :color="dialogState.confirmColor || 'primary'"
              variant="elevated"
              density="comfortable"
              @click="dialogState.onConfirm && dialogState.onConfirm()"
            >
              {{ dialogState.confirmText }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <!-- "New version available → Refresh" toast (new-deploy detector). -->
      <AppUpdateToast />

      <ScrollToTop />
    </VApp>
  </VLocaleProvider>
</template>

<style lang="scss">
.app-loading-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
}
</style>
